import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { ChevronRight, Lock, Search, Sparkles, TrendingUp } from "lucide-react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { FREE_CHAPTER_LIMIT, usePaidAccess } from "@/components/billing/feature-lock";
import { useUpgrade } from "@/components/billing/premium-lock";
import { TrendChart } from "@/components/pyq/trend-chart";
import { PyqChapterSheet } from "@/components/pyq/chapter-detail-sheet";
import {
  EXAM_LABEL,
  EXAM_SUBJECTS,
  PRIORITIES,
  PRIORITY_BY_ID,
  SORTS,
  SUBJECT_LABEL,
  TREND_LABEL,
  chapterPriority,
  filterChapters,
  getChapters,
  sortChapters,
  summarise,
  trendDirection,
  type PriorityId,
  type PyqSort,
} from "@/lib/pyq/analytics";
import type { ChapterTrend, ExamId, SubjectId } from "@/lib/pyq/types";
import { cn } from "@/lib/utils";
import { ShareButton } from "@/components/app/share-button";

export const Route = createFileRoute("/pyq-predictor")({
  head: () => ({
    ...seo({
      title: "PYQ Predictor — Chapter-wise JEE & NEET Trends",
      description:
        "Chapter-wise previous year question trends for JEE and NEET: average questions, marks, repeat probability, 5-year trend charts and preparation priority.",
      path: "/pyq-predictor",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "PYQ Predictor", path: "/pyq-predictor" },
      ]),
    ],
  }),
  component: PyqPredictorPage,
});

const CARD =
  "rounded-3xl border border-white/10 bg-card/70 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-xl";

const PILL =
  "rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all active:scale-95";

function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cn(
            PILL,
            value === o.id
              ? "border-primary/40 bg-primary/15 text-primary"
              : "border-white/10 bg-card/60 text-muted-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SummaryCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className={cn(CARD, "p-3.5")}>
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-[18px] font-extrabold tabular-nums tracking-tight">{value}</p>
      {sub ? <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{sub}</p> : null}
    </div>
  );
}

function ChapterRow({
  chapter,
  locked,
  free,
  onOpen,
}: {
  chapter: ChapterTrend;
  locked: boolean;
  free?: boolean;
  onOpen: () => void;
}) {
  const priority = PRIORITY_BY_ID.get(chapterPriority(chapter));
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-white/5 active:scale-[0.99]"
    >
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-[14px] font-bold">{chapter.chapter}</span>
          {free ? (
            <span className="shrink-0 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.1em] text-emerald-600 dark:text-emerald-300">
              Free
            </span>
          ) : locked ? (
            <Lock className="size-3.5 shrink-0 text-muted-foreground" />
          ) : null}
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-1.5">
          {priority ? (
            <span
              className={cn(
                "rounded-full border px-2 py-0.5 text-[10.5px] font-bold",
                priority.className,
              )}
            >
              {priority.emoji} {priority.label.replace(" Priority", "")}
            </span>
          ) : null}
          <span className="rounded-full border border-white/10 bg-surface px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
            Class {chapter.classLevel}
          </span>
        </span>
        <span className="mt-1.5 block truncate text-[11.5px] text-muted-foreground">
          {locked
            ? "Premium — unlock full analysis"
            : `${chapter.avgQuestions} Qs • ${chapter.avgMarks} marks • ${chapter.repeatProbability} • ${TREND_LABEL[trendDirection(chapter.recentTrend)]}`}
        </span>
      </span>
      {!locked && chapter.recentTrend?.length ? (
        <TrendChart values={chapter.recentTrend} className="h-9 w-14 shrink-0" />
      ) : null}
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

function PyqPredictorPage() {
  const { paid: isPaid } = usePaidAccess();
  const { openUpgrade } = useUpgrade();

  const [exam, setExam] = useState<ExamId>("jee");
  const subjects = EXAM_SUBJECTS[exam];
  const [subjectByExam, setSubjectByExam] = useState<Record<ExamId, SubjectId | null>>({
    jee: null,
    neet: null,
  });
  const subject = subjectByExam[exam] ?? subjects[0] ?? "physics";

  const [query, setQuery] = useState("");
  const [classLevel, setClassLevel] = useState<"all" | "11th" | "12th">("all");
  const [priority, setPriority] = useState<"all" | PriorityId>("all");
  const [sort, setSort] = useState<PyqSort>("questions");
  const [open, setOpen] = useState<ChapterTrend | null>(null);

  const chapters = useMemo(() => getChapters(exam, subject), [exam, subject]);
  const summary = useMemo(() => summarise(chapters), [chapters]);
  // Free access: the first three chapters of each subject, in dataset order.
  const freeIds = useMemo(
    () => new Set(chapters.slice(0, FREE_CHAPTER_LIMIT).map((c) => c.id)),
    [chapters],
  );
  const isLockedFor = (c: ChapterTrend) => !isPaid && !freeIds.has(c.id);
  const visible = useMemo(() => {
    const sorted = sortChapters(filterChapters(chapters, { query, classLevel, priority }), sort);
    if (isPaid) return sorted;
    // Free users: surface the free-preview chapters first, keeping the sort order within groups.
    return [...sorted.filter((c) => freeIds.has(c.id)), ...sorted.filter((c) => !freeIds.has(c.id))];
  }, [chapters, query, classLevel, priority, sort, isPaid, freeIds]);

  return (
    <main className="relative mx-auto min-h-screen w-full max-w-7xl px-4 pb-10 pt-7 sm:px-6 lg:px-8">
      <header>
        <div className="flex items-start justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-cyan-600 dark:text-cyan-300">
            <TrendingUp className="size-3" /> PYQ Predictor
          </span>
          <ShareButton />
        </div>
        <h1 className="mt-2.5 text-2xl font-extrabold tracking-tight">
          Chapter-wise PYQ trends & priority
        </h1>

        <p className="mt-1 text-[13px] text-muted-foreground">
          Pick your exam and subject to see average questions, marks, repeat probability and the
          last five years of trends.
        </p>
      </header>

      <section className="mt-6 space-y-3">
        <Segmented
          options={(Object.keys(EXAM_LABEL) as ExamId[]).map((id) => ({
            id,
            label: EXAM_LABEL[id],
          }))}
          value={exam}
          onChange={(id) => setExam(id)}
        />
        {subjects.length ? (
          <Segmented
            options={subjects.map((id) => ({ id, label: SUBJECT_LABEL[id] }))}
            value={subject}
            onChange={(id) => setSubjectByExam((prev) => ({ ...prev, [exam]: id }))}
          />
        ) : null}
      </section>

      {chapters.length === 0 ? (
        <div className={cn(CARD, "mt-6 p-6 text-center")}>
          <h2 className="text-base font-bold">No chapter data yet</h2>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {EXAM_LABEL[exam]} {SUBJECT_LABEL[subject]} trends will appear here as soon as the
            dataset is added.
          </p>
        </div>
      ) : (
        <>
          <section className="mt-6 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
            <SummaryCard label="Total chapters" value={String(summary.totalChapters)} />
            <SummaryCard label="High yield" value={String(summary.highYield)} />
            <SummaryCard label="Avg questions" value={String(summary.avgQuestions)} />
            <SummaryCard label="Avg marks" value={String(summary.avgMarks)} />
            {summary.topProbability ? (
              <div className="col-span-2 sm:col-span-3 lg:col-span-1">
                <SummaryCard
                  label="Highest repeat probability"
                  value={summary.topProbability.label}
                  sub={summary.topProbability.chapter}
                />
              </div>
            ) : null}
          </section>

          {summary.topChapters.length ? (
            <section className="mt-6">
              <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                Most important chapters
              </h2>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {summary.topChapters.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setOpen(c)}
                    className={cn(CARD, "min-w-[46%] shrink-0 p-3 text-left active:scale-95")}
                  >
                    <p className="truncate text-[13px] font-bold">{c.chapter}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {c.avgQuestions} Qs • {c.yieldCategory}
                    </p>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          <section className="mt-6 space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search chapter or subtopic"
                aria-label="Search chapter"
                className="w-full rounded-2xl border border-white/10 bg-card/60 py-3 pl-10 pr-3 text-[13px] outline-none backdrop-blur-xl placeholder:text-muted-foreground focus:border-primary/40"
              />
            </div>
            <Segmented
              options={[
                { id: "all", label: "All classes" },
                { id: "11th", label: "11th" },
                { id: "12th", label: "12th" },
              ]}
              value={classLevel}
              onChange={setClassLevel}
            />
            <Segmented
              options={[
                { id: "all" as const, label: "All priority" },
                ...PRIORITIES.map((p) => ({ id: p.id, label: `${p.emoji} ${p.label.replace(" Priority", "")}` })),
              ]}
              value={priority}
              onChange={setPriority}
            />
            <Segmented options={SORTS} value={sort} onChange={setSort} />
          </section>

          <section className="mt-5">
            {!isPaid ? (
              <div className="mb-3 flex items-start gap-2.5 rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.07] px-3.5 py-2.5">
                <Sparkles className="mt-0.5 size-3.5 shrink-0 text-emerald-600 dark:text-emerald-300" />
                <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                  <span className="block text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-600 dark:text-emerald-300">
                    Free preview
                  </span>
                  Explore {FREE_CHAPTER_LIMIT} chapters for free. Unlock Premium to access the
                  complete PYQ analysis.
                </p>
              </div>
            ) : null}
            {visible.length ? (
              <motion.ul
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="divide-y divide-white/10 overflow-hidden rounded-3xl lg:grid lg:grid-cols-2 lg:gap-x-0 lg:divide-y-0 lg:[&>li]:border-b lg:[&>li]:border-white/10 border border-white/10 bg-card/60 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-xl"
              >
                {visible.map((c) => (
                  <li key={c.id}>
                    <ChapterRow
                      chapter={c}
                      locked={isLockedFor(c)}
                      free={!isPaid && freeIds.has(c.id)}
                      onOpen={() => setOpen(c)}
                    />
                  </li>
                ))}
              </motion.ul>
            ) : (
              <p className={cn(CARD, "p-6 text-center text-[13px] text-muted-foreground")}>
                No chapters match these filters.
              </p>
            )}
          </section>
        </>
      )}

      <PyqChapterSheet
        chapter={open}
        locked={open ? isLockedFor(open) : false}
        onClose={() => setOpen(null)}
        onUpgrade={() => {
          setOpen(null);
          openUpgrade("PYQ Predictor premium chapters");
        }}
      />
    </main>
  );
}
